/**
 * AI Classification Page
 *
 * Full management UI for AI message purpose classifications with
 * settings display, stats cards, review queue, and classification history.
 */
import { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, Badge, Button } from '@respectly/ui';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@respectly/ui';
import {
  Brain,
  ArrowLeft,
  Loader2,
  Flag,
  BarChart3,
  Settings2,
  History,
  Sparkles,
  X,
  CheckCircle,
} from 'lucide-react';
import type { MessagePurpose } from '@respectly/shared-types';
import {
  usePolicyStore,
  useAiOverrideRate,
  useFlaggedClassificationsCount,
} from '../../stores/usePolicyStore';
import { AIClassificationReviewCard } from '../../components/policy/AIClassificationReviewCard';

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const cardListVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const cardItemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

// ============================================
// Component
// ============================================

export function PolicyAIClassification() {
  const aiClassifications = usePolicyStore((s) => s.aiClassifications);
  const isLoadingAiClassifications = usePolicyStore((s) => s.isLoadingAiClassifications);
  const isUpdating = usePolicyStore((s) => s.isUpdating);
  const loadAiClassifications = usePolicyStore((s) => s.loadAiClassifications);
  const overrideAiClassification = usePolicyStore((s) => s.overrideAiClassification);
  const aiClassificationFilters = usePolicyStore((s) => s.aiClassificationFilters);
  const setAiClassificationFilter = usePolicyStore((s) => s.setAiClassificationFilter);
  const clearAiClassificationFilters = usePolicyStore((s) => s.clearAiClassificationFilters);

  const overrideRate = useAiOverrideRate();
  const flaggedCount = useFlaggedClassificationsCount();

  useEffect(() => {
    loadAiClassifications();
  }, [loadAiClassifications]);

  // Derived data
  const autoClassifiedCount = useMemo(
    () => aiClassifications.filter((c) => !c.isFlagged).length,
    [aiClassifications]
  );

  const flaggedItems = useMemo(
    () => aiClassifications.filter((c) => c.isFlagged),
    [aiClassifications]
  );

  const hasActiveFilters = !!(
    aiClassificationFilters.classifiedPurpose ||
    aiClassificationFilters.dateRange
  );

  const handleOverride = (id: string, purpose: MessagePurpose) => {
    overrideAiClassification(id, purpose);
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center gap-4">
          <Link
            to="/settings/policy"
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </Link>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/40 dark:to-purple-800/40 flex items-center justify-center">
            <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              AI Classification
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Review and override AI message purpose classifications
            </p>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoadingAiClassifications ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : (
          <motion.div
            variants={cardListVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6 max-w-6xl"
          >
            {/* Section 1 — Settings Panel */}
            <motion.div variants={cardItemVariants}>
              <Card className="border-slate-200/60 dark:border-slate-700/60 border-l-4 border-l-purple-500 dark:border-l-purple-400">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Settings2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                      Classification Settings
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Confidence Threshold */}
                    <div className="space-y-1.5">
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        Confidence Threshold
                      </span>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500 rounded-full"
                            style={{ width: '70%' }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-purple-600 dark:text-purple-400 tabular-nums">
                          70%
                        </span>
                      </div>
                    </div>

                    {/* Auto-classify */}
                    <div className="space-y-1.5">
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        Auto-Classify
                      </span>
                      <div>
                        <Badge variant="success" className="text-[10px]">
                          Enabled above threshold
                        </Badge>
                      </div>
                    </div>

                    {/* Purpose Taxonomy */}
                    <div className="space-y-1.5">
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        Purpose Taxonomy
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge variant="info" className="text-[10px]">MARKETING</Badge>
                        <Badge variant="success" className="text-[10px]">INFORMATIONAL</Badge>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 italic">
                    Transactional/operational messages should be categorized as INFORMATIONAL
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Section 2 — Stats Cards */}
            <motion.div variants={cardItemVariants}>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {/* Total Classified */}
                <Card className="border-slate-200/60 dark:border-slate-700/60">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                          {aiClassifications.length}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Total Classified
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Auto-Classified */}
                <Card className="border-slate-200/60 dark:border-slate-700/60">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                          {autoClassifiedCount}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Auto-Classified
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Flagged for Review */}
                <Card className="border-slate-200/60 dark:border-slate-700/60">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <Flag className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                          {flaggedCount}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Flagged for Review
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Override Rate */}
                <Card className="border-slate-200/60 dark:border-slate-700/60">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                          {Math.round(overrideRate * 100)}%
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Override Rate
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </motion.div>

            {/* Section 3 — Review Queue */}
            <motion.div variants={cardItemVariants} className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Flag className="w-4 h-4 text-amber-500" />
                  <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                    Review Queue
                  </h2>
                  <Badge variant="warning" className="text-[10px] tabular-nums">
                    {flaggedCount}
                  </Badge>
                </div>
              </div>

              {/* Filters */}
              <div className="flex items-center gap-3 flex-wrap">
                <Select
                  value={aiClassificationFilters.classifiedPurpose ?? 'all'}
                  onValueChange={(v) =>
                    setAiClassificationFilter(
                      'classifiedPurpose',
                      v === 'all' ? undefined : (v as MessagePurpose)
                    )
                  }
                >
                  <SelectTrigger className="w-[160px] h-8 text-xs">
                    <SelectValue placeholder="Purpose" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Purposes</SelectItem>
                    <SelectItem value="MARKETING">Marketing</SelectItem>
                    <SelectItem value="INFORMATIONAL">Informational</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={aiClassificationFilters.dateRange ?? 'all'}
                  onValueChange={(v) =>
                    setAiClassificationFilter(
                      'dateRange',
                      v === 'all' ? undefined : (v as 'all' | '24h' | '7days' | '30days' | '90days')
                    )
                  }
                >
                  <SelectTrigger className="w-[140px] h-8 text-xs">
                    <SelectValue placeholder="Date Range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="24h">Last 24h</SelectItem>
                    <SelectItem value="7days">Last 7 days</SelectItem>
                    <SelectItem value="30days">Last 30 days</SelectItem>
                    <SelectItem value="90days">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAiClassificationFilters}
                    className="h-8 text-xs"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>

              {/* Flagged Items */}
              {flaggedItems.length === 0 ? (
                <Card className="border-dashed border-2 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-8 text-center">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-3">
                      <CheckCircle className="w-6 h-6 text-emerald-500" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                      All clear!
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      No classifications need review.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <motion.div
                  variants={cardListVariants}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 lg:grid-cols-2 gap-4"
                >
                  {flaggedItems.map((c) => (
                    <motion.div key={c.id} variants={cardItemVariants}>
                      <AIClassificationReviewCard
                        classification={c}
                        onOverride={handleOverride}
                        isUpdating={isUpdating}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </motion.div>

            {/* Section 4 — Classification History */}
            <motion.div variants={cardItemVariants} className="space-y-4">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                  History
                </h2>
                <Badge variant="secondary" className="text-[10px] tabular-nums">
                  {aiClassifications.length}
                </Badge>
              </div>

              {aiClassifications.length === 0 ? (
                <Card className="border-dashed border-2 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-8 text-center">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
                      <Brain className="w-6 h-6 text-slate-400" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                      No classifications yet
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      AI classifications will appear here once messages are processed.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <motion.div
                  variants={cardListVariants}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 lg:grid-cols-2 gap-4"
                >
                  {aiClassifications.map((c) => (
                    <motion.div key={c.id} variants={cardItemVariants}>
                      <AIClassificationReviewCard
                        classification={c}
                        onOverride={handleOverride}
                        isUpdating={isUpdating}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
